import { useEffect, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type {
  AdministratorUserModel,
  CreateAdministratorAccountRequest,
  UpdateAdministratorUserRequest,
} from '@/api/model'
import {
  usePostApiAdministrationAdministratorusers,
  usePatchApiAdministrationAdministratorusersAdministratorUserId,
  useDeleteApiAdministrationAdministratorusersAdministratorUserId,
  getGetApiAdministrationAdministratorusersQueryKey,
} from '@/api/generated/administrator-user/administrator-user'
import { useGetApiMe } from '@/api/generated/user/user'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'

type FormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
}

const defaultValues: FormValues = { firstName: '', lastName: '', email: '', password: '' }

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  administrator?: AdministratorUserModel | null
  onDeleted?: (administratorId: string) => void
  // AdministratorUsers.Delete — no delete button without it.
  canDelete: boolean
}

export function AdministratorDialog({ isOpen, onOpenChange, administrator, onDeleted, canDelete }: Props) {
  const { t } = useTranslation(['administrators', 'common'])
  const isEdit = !!administrator
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset, watch } = useForm<FormValues>({ defaultValues })

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  // The backend forbids deleting yourself; hide the button instead of running into a 403.
  const { data: meData } = useGetApiMe()
  const isSelf = isEdit && administrator!.id === meData?.data?.id

  // A new account is always created locked, so the create case reads as "not activated" too.
  const isActivated = isEdit && administrator!.isActivated

  useEffect(() => {
    if (isOpen) {
      reset(isEdit ? {
        firstName: administrator.firstName ?? '',
        lastName: administrator.lastName ?? '',
        email: administrator.email ?? '',
        password: '',
      } : defaultValues)
    }
  }, [isOpen, administrator])

  const invalidate = () => queryClient.invalidateQueries({
    queryKey: getGetApiAdministrationAdministratorusersQueryKey(),
  })

  const { mutate: createAdministrator, isPending: isCreating } = usePostApiAdministrationAdministratorusers({
    mutation: {
      onSuccess: () => { notify.success(t('dialog.toasts.created')); onOpenChange(false); invalidate() },
      onError: (error) => notify.apiError(error, t('dialog.toasts.createError')),
    },
  })

  const { mutate: updateAdministrator, isPending: isUpdating } = usePatchApiAdministrationAdministratorusersAdministratorUserId({
    mutation: {
      onSuccess: () => { notify.success(t('dialog.toasts.updated')); onOpenChange(false); invalidate() },
      onError: (error) => notify.apiError(error, t('dialog.toasts.updateError')),
    },
  })

  const { mutate: deleteAdministrator, isPending: isDeleting } = useDeleteApiAdministrationAdministratorusersAdministratorUserId({
    mutation: {
      onSuccess: () => {
        notify.success(t('dialog.toasts.deleted'))
        setConfirmDeleteOpen(false)
        onOpenChange(false)
        if (administrator?.id) onDeleted?.(administrator.id)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('dialog.toasts.deleteError')),
    },
  })

  const isPending = isCreating || isUpdating || isDeleting

  const onValid = (values: FormValues) => {
    if (isEdit) {
      const payload: UpdateAdministratorUserRequest = {
        firstName: values.firstName || null,
        lastName: values.lastName || null,
      }
      updateAdministrator({ administratorUserId: administrator!.id!, data: payload })
    } else {
      const payload: CreateAdministratorAccountRequest = {
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        email: values.email || null,
        password: values.password || null,
      }
      createAdministrator({ data: payload })
    }
  }

  const values = watch()
  const canSubmit = isEdit
    ? !!values.firstName && !!values.lastName
    : !!values.firstName && !!values.lastName && !!values.email && !!values.password

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? t('dialog.titleEdit') : t('dialog.titleCreate')}</DialogTitle>
            <DialogDescription>
              {isEdit ? t('dialog.descriptionEdit') : t('dialog.descriptionCreate')}
            </DialogDescription>
          </DialogHeader>

          <form id="administrator-form" onSubmit={handleSubmit(onValid)} className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="firstName">{t('dialog.firstName')}</Label>
              <Input id="firstName" {...register('firstName')} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="lastName">{t('dialog.lastName')}</Label>
              <Input id="lastName" {...register('lastName')} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">{t('dialog.email')}</Label>
              {/* Email is fixed after creation, the backend has no rename for it. */}
              <Input id="email" type="email" disabled={isEdit} {...register('email')} />
            </div>
            {!isEdit && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password">{t('dialog.password')}</Label>
                <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
                <p className="text-xs text-muted-foreground">{t('dialog.passwordHint')}</p>
              </div>
            )}

            {/* Like the list: only the exception is shown. Also covers create, where the account
                is always still locked. */}
            {!isActivated && (
              <div className="flex items-center gap-2 rounded-md bg-muted p-2.5 text-xs text-muted-foreground">
                <TriangleAlert className="size-4 shrink-0 text-red-500" />
                <span>{t('dialog.status.inactive')}</span>
              </div>
            )}
          </form>

          <DialogFooter className={isEdit && !isSelf && canDelete ? 'flex-row justify-between sm:justify-between' : undefined}>
            {isEdit && !isSelf && canDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={isPending}
              >
                {t('buttons.delete', { ns: 'common' })}
              </Button>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" form="administrator-form" disabled={!canSubmit || isPending}>
                {isEdit ? t('buttons.save', { ns: 'common' }) : t('dialog.create')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title={t('dialog.confirmDelete.title')}
        description={t('dialog.confirmDelete.description', {
          name: [administrator?.firstName, administrator?.lastName].filter(Boolean).join(' '),
        })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => administrator?.id && deleteAdministrator({ administratorUserId: administrator.id })}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </>
  )
}
